import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ClaimWithActions, WorkData } from './types';
import styled from 'astroturf/react';
import {
  CiterightContext,
  CiterightContextProvider,
  useUrlParam,
} from './CiterightContextProvider';
import { Button } from 'js/design_system/Button';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Spinner } from 'js/design_system/Spinner';
import { Skeleton } from 'js/design_system/skeleton/Skeleton';
import { Banner } from 'js/design_system/Banner';
import { Icon } from 'js/design_system/Icon';
import Badge from 'js/design_system/Badge';
import WorkCard from './components/WorkCard';
import pluralize from 'pluralize';
import { trackClick } from 'js/utils/trackClick';
import { getDefaultSortSettings } from './utils/sortDefaults';
import { getPriorityLabel, getPriorityColor } from './utils/claimPriority';
import type { SortSettings } from './components/SortPanel';
import { useReportXml } from './hooks/useReportXml';
import { useNoClaimsToast } from './hooks/useNoClaimsToast';
import { useFirstSuggestedWorkToast } from './hooks/useFirstSuggestedWorkToast';
import { useGrobidXml } from './hooks/useGrobidXml';
import TopBar from './components/TopBar';
import { InlineClaimStatusIcon } from './components/InlineClaimStatusIcon';
import { DebugPage } from './DebugPage';
import { OverviewPage } from './OverviewPage';
import { useWorkIdInitialization } from './hooks/useWorkIdInitialization';
import { useClaimNavigation } from './hooks/useClaimNavigation';
import { useTabVisibilityMilestones } from './hooks/useTabVisibilityMilestones';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import FilterBar, {
  type ClaimFilter,
  type FilterBarSettings,
} from './components/FilterBar';
import {
  CiterightLanding,
  type Testimonial,
  type FAQ,
  type UserWork,
} from './CiterightLanding';

const PageWrapper = styled.div`
  background-color: #f9f7f4;
  min-height: 100vh;
`;

const LayoutWrapper = styled.div`
  display: flex;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 899px) {
    flex-direction: column;
    gap: 16px;
    padding: 12px;
    flex-direction: column-reverse;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Sidebar = styled.aside`
  width: 400px;
  flex-shrink: 0;
  position: sticky;
  top: 68px;
  max-height: calc(100vh - 68px);
  overflow-y: auto;
  padding: 16px 16px 32px 16px;

  @media (max-width: 899px) {
    width: 100%;
    position: static;
    max-height: none;
    padding: 0;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 80ch;
  margin: 0 auto;
  font-family: 'Georgia', serif;
  padding: 0 16px 16px 16px;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 16px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ClaimContainer = styled.div`
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-weight: 400;
  margin: 16px 0px;
  padding: 24px;
  background: var(--background-white, #ffffff);
  border: 1px solid #e6ecf7;

  @media (max-width: 768px) {
    padding: 12px 16px;
    margin: 12px 0px;
  }
`;

const SearchResultsContainer = styled.div`
  margin-top: 12px;
`;

const WorkCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  @media (max-width: 768px) {
    padding: 4px 0;
  }
`;

const ScrollableWorksWrapper = styled.div`
  position: relative;
`;

const ScrollableWorksContainer = styled.div`
  max-height: 900px;
  overflow-y: auto;
  position: relative;

  @media (max-width: 768px) {
    max-height: 600px;
  }
`;

const ScrollFadeTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 1),
    rgba(255, 255, 255, 0)
  );
  pointer-events: none;
  z-index: 10;
  transition: opacity 0.3s ease;
`;

const ScrollFadeBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 1),
    rgba(255, 255, 255, 0)
  );
  pointer-events: none;
  z-index: 10;
  transition: opacity 0.3s ease;
`;

const Section = styled.section`
  margin: 0px 0;
`;

const SectionHeading = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 24px 0 12px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 18px;
    margin: 20px 0 10px;
  }
`;

const Paragraph = styled.div`
  margin: 12px 0;
  line-height: 1.6;
  text-align: justify;
  font-weight: 500;
`;

const ClaimText = styled.span`
  background-color: #fee940;
  cursor: pointer;
  padding: 4px 0px;
  text-decoration: underline dotted;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #ffd633;
  }
`;

const ExistingCitationText = styled.span`
  background-color: #ffd4b3;
  cursor: pointer;
  padding: 4px 0px;
  text-decoration: underline dotted;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #ffb890;
  }
`;

const SelectionButton = styled.button`
  position: absolute;
  background: var(--background-white, #ffffff);
  border: 1px solid #e6ecf7;
  border-radius: 4px;
  padding: 8px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #d0d0d0;
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 768px) {
    padding: 10px 18px;
    font-size: 15px;
    min-height: 44px;
  }
`;

const ClaimDetails = ({
  claim,
  filterSettings,
}: {
  claim: ClaimWithActions;
  onCitationClick: (workId: string) => void;
  filterSettings: FilterBarSettings;
}) => {
  const context = useContext(CiterightContext);
  // Find the original claim from the report that has actions
  const claimWithActions = useMemo(() => {
    const reportClaims = context?.report?.claims || [];
    return reportClaims.find((c) => c.id === claim.id) || claim;
  }, [context?.report?.claims, claim]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showFiltered, setShowFiltered] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [timeToShowSkeleton, setTimeToShowSkeleton] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (context?.isPolling && !claimWithActions.search?.done) {
      const timeout = setTimeout(() => setTimeToShowSkeleton(true), 3000);
      return () => clearTimeout(timeout);
    }
  }, [context?.isPolling, claimWithActions.search?.done, claim.id]);

  // Get referenced works from the report
  const referencedWorks = useMemo(() => {
    if (!claim.reference_ids || claim.reference_ids.length === 0) {
      return [];
    }
    const reportReferences = context?.report?.references || [];
    return reportReferences.filter((ref) =>
      claim.reference_ids.includes(ref.reference_identifier ?? ''),
    );
  }, [claim.reference_ids, context?.report?.references]);

  // Filter and sort works based on filter settings
  const filteredAndSortedWorks = useMemo(() => {
    const suggestedWorks = claimWithActions.search?.suggested_works || [];
    if (suggestedWorks.length === 0) return [];

    // Apply year filter first
    let filtered = suggestedWorks;
    if (filterSettings.minYear !== null || filterSettings.maxYear !== null) {
      filtered = filtered.filter((work) => {
        const publicationYear = work.work.publicationYear;
        if (publicationYear === null || publicationYear === undefined) {
          return false;
        }

        const meetsMinYear =
          filterSettings.minYear === null ||
          publicationYear >= filterSettings.minYear;
        const meetsMaxYear =
          filterSettings.maxYear === null ||
          publicationYear <= filterSettings.maxYear;

        return meetsMinYear && meetsMaxYear;
      });
    }

    // Get effective sort settings (user-selected or default)
    const effectiveSortSettings: SortSettings =
      filterSettings.sortSettings || getDefaultSortSettings();

    // Apply sorting
    if (effectiveSortSettings.field === 'relevance') {
      // Keep original order for basic relevance
      return filtered;
    }

    // Helper to get sort value based on field
    const getSortValue = (work: WorkData): number => {
      switch (effectiveSortSettings.field) {
        case 'relevance_score':
          return work.relevance_score ?? 0;
        case 'citationCount':
          return work.work.citationCount ?? 0;
        case 'publicationYear':
          return work.work.publicationYear ?? 0;
        case 'impactFactor':
          return work.impact_factor?.impact_factor ?? 0;
        default:
          return 0;
      }
    };

    // Create indexed array for stable sorting
    const indexed = filtered.map((item, index) => ({ item, index }));

    indexed.sort((a, b) => {
      const aValue = getSortValue(a.item);
      const bValue = getSortValue(b.item);

      if (aValue === bValue) {
        // Use original index as tiebreaker for stable sort
        return a.index - b.index;
      }

      return effectiveSortSettings.direction === 'desc'
        ? bValue - aValue
        : aValue - bValue;
    });

    return indexed.map(({ item }) => item);
  }, [
    claimWithActions.search?.suggested_works,
    filterSettings.minYear,
    filterSettings.maxYear,
    filterSettings.sortSettings,
  ]);

  // Track filtering information for user feedback
  const filteringInfo = useMemo(() => {
    const originalCount = claimWithActions.search?.suggested_works?.length || 0;
    const filteredCount = filteredAndSortedWorks.length;
    const isFilterActive =
      filterSettings.minYear !== null || filterSettings.maxYear !== null;
    const filteredOutCount = originalCount - filteredCount;

    return {
      originalCount,
      filteredCount,
      filteredOutCount,
      isFilterActive,
      hasFilteredOut: isFilterActive && filteredOutCount > 0,
    };
  }, [
    claimWithActions.search?.suggested_works,
    filteredAndSortedWorks.length,
    filterSettings.minYear,
    filterSettings.maxYear,
  ]);

  // Compute which works were filtered out
  const filteredOutWorks = useMemo(() => {
    if (!filteringInfo.hasFilteredOut) {
      return [];
    }
    const allWorks = claimWithActions.search?.suggested_works || [];
    const filteredWorkIds = new Set(
      filteredAndSortedWorks.map((w) => w.work.id),
    );
    return allWorks.filter((work) => !filteredWorkIds.has(work.work.id));
  }, [
    filteringInfo.hasFilteredOut,
    claimWithActions.search?.suggested_works,
    filteredAndSortedWorks,
  ]);

  // Display either first 3 or all works
  const works = useMemo(() => {
    return showAll
      ? filteredAndSortedWorks
      : filteredAndSortedWorks.slice(0, 3);
  }, [filteredAndSortedWorks, showAll]);

  // Handle scroll to show/hide fade gradients
  useEffect(() => {
    const checkScroll = () => {
      if (!scrollContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;

      // Show top fade when scrolled down
      setShowTopFade(scrollTop > 10);

      // Show bottom fade when there's more content below
      setShowBottomFade(scrollTop + clientHeight < scrollHeight - 10);
    };

    const container = scrollContainerRef.current;
    if (container) {
      // Check initial state
      checkScroll();

      // Add scroll listener
      container.addEventListener('scroll', checkScroll);

      // Re-check when content changes
      setTimeout(checkScroll, 100);

      return () => {
        container.removeEventListener('scroll', checkScroll);
      };
    }
  }, [works.length, showAll]);

  const handleSearch = useCallback(() => {
    trackClick({
      source: 'citeright.inline_claim_search_button',
      entityId: context?.reportId ?? undefined,
    });

    if (!claimWithActions.actions) {
      return;
    }

    setIsSearching(true);
    const searchDeferred = claimWithActions.actions.search();
    searchDeferred.promise
      .catch((error: Error) => {
        console.error('Failed to search claim:', error);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }, [claimWithActions, context?.reportId]);

  return (
    <ImpressionWatcher
      as={ClaimContainer}
      source="citeright.claim_details"
      entity_id={context?.reportId || undefined}
      extra={{ claim_id: claim.id }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          padding: '0 8px',
        }}
      >
        <div style={{ fontSize: 14, flex: 1, textAlign: 'left' }}>
          {claim.reason || claim.text}
        </div>
        {(claim.priority || claim.top_claims_count) && (
          <Badge
            text={getPriorityLabel(claim.priority, claim.top_claims_count)}
            backgroundColor={
              getPriorityColor(claim.priority, claim.top_claims_count)
                .backgroundColor
            }
            color={
              getPriorityColor(claim.priority, claim.top_claims_count).color
            }
            style={{ flexShrink: 0 }}
          />
        )}
      </div>

      {context?.canEdit &&
        claimWithActions.search?.suggested_works === null && (
          <div style={{ marginTop: 12, padding: '0 8px' }}>
            <Button
              size="sm"
              onClick={handleSearch}
              iconRight="search"
              disabled={
                claimWithActions.search?.status === 'pending' || isSearching
              }
            >
              Search for citations
            </Button>
          </div>
        )}
      {context?.isPolling && !claimWithActions.search?.done && (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '0 8px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner size={16} />
            <Body size="sm" className="ds2-5-body-link-secondary">
              Finding references that support your claim...
            </Body>
          </div>
          {timeToShowSkeleton &&
            (claimWithActions.search?.suggested_works ?? []).length === 0 && (
              <div style={{ marginTop: 12 }}>
                <Skeleton type="citeright-suggested-work" />
              </div>
            )}
        </div>
      )}
      {claimWithActions.search?.done &&
        claimWithActions.search?.suggested_works !== null &&
        works.length === 0 && (
          <span
            style={{ textAlign: 'center', margin: '12px 0', padding: '0 8px' }}
          >
            <Body size="md" style={{ color: 'var(--text-secondary)' }}>
              {filteringInfo.hasFilteredOut ? (
                <>
                  Filtered {filteringInfo.originalCount}{' '}
                  {pluralize('reference', filteringInfo.originalCount)}.
                </>
              ) : (
                'No references found for this query.'
              )}
            </Body>
          </span>
        )}
      {works.length > 0 && (
        <SearchResultsContainer>
          <Body size="sm" bold style={{ padding: '0 8px' }}>
            Top suggested references
          </Body>
          <ScrollableWorksWrapper>
            <ScrollableWorksContainer ref={scrollContainerRef}>
              <ImpressionWatcher
                as={WorkCardsContainer}
                source="citeright.search_results"
                entity_id={context?.reportId || undefined}
                extra={{
                  claim_id: claim.id,
                  work_count: works.length,
                }}
              >
                {works.map((work: WorkData, index: number) => (
                  <WorkCard
                    key={index}
                    workData={work}
                    isHighlighted={false}
                    showAddToListButton={context?.canEdit ?? true}
                    showPaperEvaluationChatButton={false}
                    claimId={claim.id}
                    claimText={claim.text}
                    pageContext="inline_report"
                  />
                ))}
              </ImpressionWatcher>
            </ScrollableWorksContainer>
            <ScrollFadeTop style={{ opacity: showTopFade ? 1 : 0 }} />
            <ScrollFadeBottom style={{ opacity: showBottomFade ? 1 : 0 }} />
          </ScrollableWorksWrapper>
          {!showAll && filteredAndSortedWorks.length > 3 && (
            <span style={{ textAlign: 'center' }}>
              <BodyLink
                href="#"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  trackClick({
                    source: 'citeright.inline_view_more_references_button',
                    entityId: context?.reportId ?? undefined,
                    extra: {
                      claim_id: claim.id,
                      report_id: context?.reportId || undefined,
                    },
                  });
                  setShowAll(true);
                }}
              >
                View {filteredAndSortedWorks.length - 3} more{' '}
                {pluralize('reference', filteredAndSortedWorks.length - 3)}{' '}
                <Icon
                  name="expand_more"
                  size={16}
                  style={{ verticalAlign: 'middle' }}
                />
              </BodyLink>
            </span>
          )}
          {filteringInfo.hasFilteredOut && !showFiltered && (
            <span
              style={{
                textAlign: 'center',
                marginTop: 8,
                display: 'block',
              }}
            >
              <BodyLink
                href="#"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  trackClick({
                    source: 'citeright.inline_show_filtered_references_button',
                    entityId: context?.reportId ?? undefined,
                    extra: {
                      claim_id: claim.id,
                      report_id: context?.reportId || undefined,
                      filtered_count: filteredOutWorks.length,
                    },
                  });
                  setShowFiltered(true);
                }}
              >
                Show {filteredOutWorks.length} filtered{' '}
                {pluralize('suggestion', filteredOutWorks.length)}{' '}
                <Icon
                  name="expand_more"
                  size={16}
                  style={{ verticalAlign: 'middle' }}
                />
              </BodyLink>
            </span>
          )}
          {showFiltered && filteredOutWorks.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Body
                size="sm"
                bold
                style={{ padding: '0 8px', marginBottom: 4 }}
              >
                Filtered suggestions
              </Body>
              <WorkCardsContainer>
                {filteredOutWorks.map((work: WorkData, index: number) => (
                  <WorkCard
                    key={index}
                    workData={work}
                    isHighlighted={false}
                    showAddToListButton={true}
                    showPaperEvaluationChatButton={false}
                    claimId={claim.id}
                    claimText={claim.text}
                    isFiltered={true}
                    pageContext="inline_report"
                  />
                ))}
              </WorkCardsContainer>
              <span
                style={{ textAlign: 'center', marginTop: 8, display: 'block' }}
              >
                <BodyLink
                  href="#"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    trackClick({
                      source:
                        'citeright.inline_hide_filtered_references_button',
                      entityId: context?.reportId ?? undefined,
                      extra: {
                        claim_id: claim.id,
                        report_id: context?.reportId || undefined,
                      },
                    });
                    setShowFiltered(false);
                  }}
                >
                  Hide filtered suggestions{' '}
                  <Icon
                    name="expand_less"
                    size={16}
                    style={{ verticalAlign: 'middle' }}
                  />
                </BodyLink>
              </span>
            </div>
          )}
        </SearchResultsContainer>
      )}

      {referencedWorks.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Body size="sm" bold style={{ marginBottom: 4, padding: '0 8px' }}>
            Your {pluralize('Reference', referencedWorks.length, false)}
          </Body>
          <WorkCardsContainer>
            {referencedWorks.map((workData, index) => (
              <WorkCard
                key={index}
                workData={workData}
                isHighlighted={false}
                showAddToListButton={true}
                showPaperEvaluationChatButton={false}
                pageContext="inline_report"
              />
            ))}
          </WorkCardsContainer>
        </div>
      )}
    </ImpressionWatcher>
  );
};

const ClaimPart = ({
  claim,
  forceExpanded = false,
  claimRef,
  onCitationClick,
  filterSettings,
  onClaimExpand,
}: {
  claim: ClaimWithActions;
  forceExpanded?: boolean;
  claimRef?: React.RefObject<HTMLSpanElement>;
  onCitationClick: (workId: string) => void;
  filterSettings: FilterBarSettings;
  onClaimExpand?: (claimId: string) => void;
}) => {
  const context = useContext(CiterightContext);
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);

  const expanded = userExpanded ?? forceExpanded;

  useEffect(() => {
    if (!forceExpanded && userExpanded === false) {
      setUserExpanded(null);
    }
  }, [forceExpanded, userExpanded]);

  const handleToggle = useCallback(() => {
    const newExpanded = !expanded;
    setUserExpanded(newExpanded);
    // Update current claim when expanding
    if (newExpanded && onClaimExpand) {
      onClaimExpand(claim.id);
    }
  }, [expanded, onClaimExpand, claim.id]);

  const HighlightComponent =
    claim.source === 'extract_references_agent'
      ? ExistingCitationText
      : ClaimText;

  return (
    <ImpressionWatcher
      as="span"
      key={claim.id}
      source="citeright.claim_text"
      entity_id={context?.reportId || undefined}
      extra={{
        claim_id: claim.id,
      }}
    >
      <span ref={claimRef}>
        <HighlightComponent onClick={handleToggle}>
          {claim.text}
          <InlineClaimStatusIcon claim={claim} />
        </HighlightComponent>
      </span>
      {expanded && (
        <ClaimDetails
          claim={claim}
          onCitationClick={onCitationClick}
          filterSettings={filterSettings}
        />
      )}
    </ImpressionWatcher>
  );
};

const ParagraphWithClaims = ({
  item,
  firstClaimWithWorksId,
  forceExpandedClaimId,
  claimRefs,
  onCitationClick,
  claimFilter,
  filterSettings,
  onClaimExpand,
}: {
  item: { type: 'heading' | 'paragraph'; parts: (string | ClaimWithActions)[] };
  firstClaimWithWorksId?: string | null;
  forceExpandedClaimId?: string | null;
  claimRefs: Map<string, React.RefObject<HTMLSpanElement>>;
  onCitationClick: (workId: string) => void;
  claimFilter: ClaimFilter;
  filterSettings: FilterBarSettings;
  onClaimExpand?: (claimId: string) => void;
}) => {
  return (
    <Paragraph>
      {item.parts.map((part, index) => {
        if (typeof part === 'string') {
          return <span key={index}>{part}</span>;
        } else {
          // Filter claims based on priority
          if (claimFilter === 'essential' && part.priority !== 'High') {
            return <span key={index}>{part.text}</span>;
          }
          if (claimFilter === 'optional' && part.priority !== 'Low') {
            return <span key={index}>{part.text}</span>;
          }

          const isFirstClaimWithWorks = part.id === firstClaimWithWorksId;
          const isForceExpanded = part.id === forceExpandedClaimId;
          return (
            <ClaimPart
              key={index}
              claim={part}
              forceExpanded={isForceExpanded || isFirstClaimWithWorks}
              claimRef={claimRefs.get(part.id)}
              onCitationClick={onCitationClick}
              filterSettings={filterSettings}
              onClaimExpand={onClaimExpand}
            />
          );
        }
      })}
    </Paragraph>
  );
};

const truncateText = (text: string, maxLength: number = 235): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

interface ClaimSummaryItemProps {
  claim: ClaimWithActions;
  newSuggestionsCount: number;
  onClaimClick: (claimId: string) => void;
}

const ClaimSummaryItem = ({
  claim,
  newSuggestionsCount,
  onClaimClick,
}: ClaimSummaryItemProps) => {
  const context = useContext(CiterightContext);
  const truncatedText = truncateText(claim.text);

  return (
    <div
      style={{
        padding: 'var(--spacing-xs-12) var(--spacing-xs-16)',
        border: '1px solid var(--brand-colors-blue-300)',
        borderRadius: '8px',
        marginBottom: '8px',
        backgroundColor: 'var(--background-white, #ffffff)',
      }}
    >
      <Body size="sm" style={{ marginBottom: 4 }}>
        We found <strong>{newSuggestionsCount}</strong>{' '}
        {pluralize('reference', newSuggestionsCount)} to support{' '}
        <span style={{ fontStyle: 'italic' }}>"{truncatedText}"</span> that you
        did not cite.
        <br />
        <BodyLink
          href="#"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            trackClick({
              source: 'citeright.references_section_claim_link',
              entityId: context?.reportId ?? undefined,
              extra: { claim_id: claim.id, report_id: context?.reportId },
            });
            onClaimClick(claim.id);
          }}
          style={{
            color: 'var(--brand-colors-blue-600)',
            textDecoration: 'underline',
            fontWeight: 700,
          }}
        >
          View {newSuggestionsCount} new{' '}
          {pluralize('reference', newSuggestionsCount)}
          <Icon
            name="arrow_upward"
            size={16}
            style={{ verticalAlign: 'middle', marginLeft: 2 }}
          />
        </BodyLink>
      </Body>
    </div>
  );
};

interface ReferencesSectionProps {
  references: WorkData[];
  claims: ClaimWithActions[];
  onClaimClick: (claimId: string) => void;
}

const ReferencesSection = ({
  references,
  claims,
  onClaimClick,
}: ReferencesSectionProps) => {
  const [showExistingReferences, setShowExistingReferences] = useState(false);
  const context = useContext(CiterightContext);

  // Calculate claims with new (uncited) suggestions
  const claimsWithNewSuggestions = useMemo(() => {
    if (!claims || claims.length === 0) return [];
    if (!references) return [];

    // Build set of already-cited work titles once (lowercase for comparison)
    const existingWorkTitles = new Set(
      references
        .map((ref) => ref.work?.title?.toLowerCase())
        .filter((title) => title),
    );

    return claims
      .map((claim) => {
        // Must have completed search with suggested works
        if (
          !claim.search?.suggested_works ||
          claim.search.suggested_works.length === 0 ||
          !claim.search.done
        ) {
          return null;
        }

        // Count how many suggested works are NEW (not already cited by title)
        const newSuggestionsCount = claim.search.suggested_works.filter(
          (work) =>
            !work.work?.title ||
            !existingWorkTitles.has(work.work.title.toLowerCase()),
        ).length;

        // Only include claims with at least one new suggestion
        if (newSuggestionsCount === 0) {
          return null;
        }

        return {
          claim,
          newSuggestionsCount,
        };
      })
      .filter(
        (
          item,
        ): item is { claim: ClaimWithActions; newSuggestionsCount: number } =>
          item !== null,
      )
      .sort((a, b) => {
        // Sort by document order (chunk_index, then claim_index)
        if (a.claim.chunk_index !== b.claim.chunk_index) {
          return a.claim.chunk_index - b.claim.chunk_index;
        }
        return a.claim.claim_index - b.claim.claim_index;
      });
  }, [claims, references]);

  // Early return if nothing to show
  if (
    claimsWithNewSuggestions.length === 0 &&
    (!references || references.length === 0)
  ) {
    return null;
  }

  return (
    <Section
      style={{
        marginTop: 48,
        paddingTop: 24,
        borderTop: '2px solid var(--brand-colors-blue-300)',
      }}
    >
      <SectionHeading>References</SectionHeading>

      {/* Claims Summary Section - Always visible if present */}
      {claimsWithNewSuggestions.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Body
            size="md"
            bold
            style={{
              marginBottom: 12,
            }}
          >
            New references found for your claims
          </Body>
          <WorkCardsContainer>
            {claimsWithNewSuggestions.map(({ claim, newSuggestionsCount }) => (
              <ClaimSummaryItem
                key={claim.id}
                claim={claim}
                newSuggestionsCount={newSuggestionsCount}
                onClaimClick={onClaimClick}
              />
            ))}
          </WorkCardsContainer>
        </div>
      )}

      {/* Existing References Section - Collapsed by default */}
      {references && references.length > 0 && (
        <div>
          <BodyLink
            href="#"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              trackClick({
                source: 'citeright.references_section_toggle_existing',
                entityId: context?.reportId ?? undefined,
                extra: {
                  show: !showExistingReferences,
                  references_count: references.length,
                  report_id: context?.reportId,
                },
              });
              setShowExistingReferences(!showExistingReferences);
            }}
          >
            {showExistingReferences ? 'Hide' : 'Show'} {references.length}{' '}
            existing {pluralize('reference', references.length)}{' '}
            <Icon
              name={showExistingReferences ? 'expand_less' : 'expand_more'}
              size={16}
              style={{ verticalAlign: 'middle' }}
            />
          </BodyLink>

          {showExistingReferences && (
            <WorkCardsContainer style={{ marginTop: 12 }}>
              {references.map((workData, index) => (
                <div key={index} id={`reference-${workData.work.id}`}>
                  <WorkCard
                    workData={workData}
                    isHighlighted={false}
                    showAddToListButton={false}
                    showPaperEvaluationChatButton={false}
                    pageContext="inline_report"
                  />
                </div>
              ))}
            </WorkCardsContainer>
          )}
        </div>
      )}
    </Section>
  );
};

const UnmatchedClaimsSection = ({ claims }: { claims: ClaimWithActions[] }) => {
  if (!claims || claims.length === 0) {
    return null;
  }

  return (
    <Section
      style={{ marginTop: 48, paddingTop: 24, borderTop: '2px solid #e6ecf7' }}
    >
      <SectionHeading>Unmatched Claims ({claims.length})</SectionHeading>
      <Body
        size="sm"
        style={{ marginBottom: 16, color: 'var(--text-secondary)' }}
      >
        These claims could not be found in the document text.
      </Body>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {claims.map((claim) => (
          <ClaimContainer key={claim.id}>
            <div style={{ marginBottom: 8 }}>
              <Body size="sm" bold>
                Claim text:
              </Body>
              <Body size="sm" style={{ marginTop: 4 }}>
                {claim.text}
              </Body>
            </div>
            {claim.reason && (
              <div>
                <Body size="sm" bold>
                  Reason:
                </Body>
                <Body size="sm" style={{ marginTop: 4 }}>
                  {claim.reason}
                </Body>
              </div>
            )}
          </ClaimContainer>
        ))}
      </div>
    </Section>
  );
};

interface CiterightInlineContentProps {
  userWorks?: UserWork[];
  testimonials?: Testimonial[];
  faqs?: FAQ[];
  showPreviousReports?: boolean;
}

const CiterightInlineContent: React.FC<CiterightInlineContentProps> = ({
  userWorks: userWorksProp,
  testimonials: testimonialsProp,
  faqs: faqsProp,
  showPreviousReports,
}) => {
  // Use null coalescing to handle both null and undefined from Rails
  const userWorks = userWorksProp ?? [];
  const testimonials = testimonialsProp ?? [];
  const faqs = faqsProp ?? [];
  const context = useContext(CiterightContext);
  const reportId = context?.reportId ?? null;
  const setReportId = context?.setReportId;
  const report = context?.report;
  const canEdit = context?.canEdit ?? true;

  // Read work_id directly from URL
  const { value: workId, setValue: setWorkId } = useUrlParam('work_id');

  // Text selection state
  const [selectionState, setSelectionState] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const selectionButtonRef = useRef<HTMLButtonElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const overviewRef = useRef<HTMLElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);

  // Use the work ID initialization hook
  const { isCreatingFromWork, error: workInitError } = useWorkIdInitialization(
    workId,
    reportId,
    setReportId!,
  );

  const {
    xml,
    isLoading: xmlLoading,
    error: xmlError,
  } = useReportXml(reportId, context?.publicToken);

  // Wait for document to be loaded before showing toast
  useNoClaimsToast({ isDocumentLoaded: !xmlLoading && xml !== null });

  useTabVisibilityMilestones(reportId, context?.publicToken);

  // Callback to analyze a work without page reload
  const handleAnalyzeWork = useCallback(
    (workIdToAnalyze: number) => {
      setWorkId(workIdToAnalyze.toString());
    },
    [setWorkId],
  );

  // Callback when a report is created from upload (avoids page reload)
  const handleReportCreated = useCallback(
    (newReportId: string) => {
      setReportId?.(newReportId);
    },
    [setReportId],
  );

  const [page, setPage] = useState<'triage' | 'debug' | 'overview'>('triage');

  // Filter and sort settings
  const [filterSettings, setFilterSettings] = useState<FilterBarSettings>({
    claimFilter: 'all',
    minYear: null,
    maxYear: null,
    sortSettings: null,
  });

  // Track which claim should be force-expanded (for toast notifications)
  const [forceExpandedClaimId, setForceExpandedClaimId] = useState<
    string | null
  >(null);

  // Scroll helper function that uses topBarRef to get actual height
  const scrollTo = useCallback((element: HTMLElement, offset: number = 20) => {
    const topBarHeight = topBarRef.current?.offsetHeight || 0;
    const elementTop =
      element.getBoundingClientRect().top +
      window.pageYOffset -
      (topBarHeight + offset);
    window.scrollTo({
      top: elementTop,
      behavior: 'smooth',
    });
  }, []);

  // Create refs for all claims
  const claimRefs = useMemo(() => {
    const refs = new Map<string, React.RefObject<HTMLSpanElement>>();
    if (report) {
      report.claims.forEach((claim) => {
        refs.set(claim.id, React.createRef<HTMLSpanElement>());
      });
    }
    return refs;
  }, [report]);

  // Scroll to a claim and expand it (used by navigation and toast notifications)
  const scrollToId = useRef<string | null>(null);
  const rawScrollToId = useCallback(() => {
    if (scrollToId.current) {
      const claimRef = claimRefs.get(scrollToId.current);
      if (claimRef?.current) {
        scrollTo(claimRef.current);
        scrollToId.current = null;
      }
    }
  }, [claimRefs, scrollTo]);

  // try to scroll to the claim id if it hasn't already been scrolled to and react re-renders
  useEffect(() => {
    rawScrollToId();
  });

  const scrollToAndExpandClaim = useCallback(
    (claimId: string) => {
      // Set the force-expanded state
      setForceExpandedClaimId(claimId);

      scrollToId.current = claimId;
      rawScrollToId();
    },
    [scrollToId, setForceExpandedClaimId, rawScrollToId],
  );

  // Claim navigation state (handles URL deep linking internally)
  const {
    currentClaimIndex,
    setClaimById,
    goToMostImportant,
    goToNext,
    goToPrevious,
  } = useClaimNavigation({
    // we wait to load navigation until the XML is loaded so we can actually scroll to the claim
    claims: xmlLoading || !xml ? [] : report?.claims,
    onNavigate: scrollToAndExpandClaim,
  });

  // Handler for clicking citations to scroll to references
  const handleCitationClick = useCallback(
    (workId: string) => {
      const element = document.getElementById(`reference-${workId}`);
      if (element) {
        scrollTo(element);
      }
    },
    [scrollTo],
  );

  const handleTogglePage = useCallback((newPage: 'triage' | 'debug') => {
    setPage(newPage);
  }, []);

  const handleNavigateToOverview = useCallback(() => {
    if (page === 'overview') {
      setPage('triage');
      return;
    } else {
      setPage('overview');
      // Scroll to overview after state update
      setTimeout(() => {
        if (overviewRef.current) {
          scrollTo(overviewRef.current, 0);
        }
      }, 100);
    }
  }, [page, scrollTo]);

  // Handler for when a claim is manually expanded
  const handleClaimExpand = useCallback(
    (claimId: string) => {
      setClaimById(claimId);
    },
    [setClaimById],
  );

  // Parse document with claims using useGrobidXml hook
  const parsedDoc = useGrobidXml(xml || '', report?.claims || []);

  // Calculate claim counts for filter
  const claimCounts = useMemo(() => {
    const allClaims = report?.claims || [];
    return {
      essential: allClaims.filter((c) => c.priority === 'High').length,
      optional: allClaims.filter((c) => c.priority === 'Low').length,
      total: allClaims.length,
    };
  }, [report?.claims]);

  // Show toast when claims get their first suggested work
  useFirstSuggestedWorkToast(claimRefs, setClaimById);

  // Find the first claim with suggested works
  const firstClaimWithWorksId = useMemo(() => {
    if (!report) return null;

    for (const claim of report.claims) {
      if (
        claim.search?.suggested_works &&
        claim.search.suggested_works.length > 0
      ) {
        return claim.id;
      }
    }
    return null;
  }, [report]);

  // Scroll to and expand the first claim with suggested works (once)
  useEffect(() => {
    if (sessionStorage.getItem('scrolledTo') === firstClaimWithWorksId) {
      return;
    }
    if (firstClaimWithWorksId) {
      sessionStorage.setItem('scrolledTo', firstClaimWithWorksId);
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max (50 * 100ms)

      // Retry finding and scrolling to the ref every 100ms
      const intervalId = setInterval(() => {
        const claimRef = claimRefs.get(firstClaimWithWorksId);
        if (claimRef?.current) {
          scrollTo(claimRef.current);
          clearInterval(intervalId);
        } else {
          attempts++;
          if (attempts >= maxAttempts) {
            clearInterval(intervalId);
          }
        }
      }, 100);

      // Return cleanup function to clear interval if effect re-runs
      return () => clearInterval(intervalId);
    }
  }, [firstClaimWithWorksId, claimRefs, scrollTo]);

  // Handle text selection
  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        setSelectionState(null);
        return;
      }

      const selectedText = selection.toString().trim();
      if (!selectedText || selectedText.split(' ').length < 3) {
        setSelectionState(null);
        return;
      }

      const range = selection.getRangeAt(0);

      // Check if the selection is within the MainContent element
      if (
        mainContentRef.current &&
        !mainContentRef.current.contains(range.commonAncestorContainer)
      ) {
        setSelectionState(null);
        return;
      }

      const rect = range.getBoundingClientRect();

      // Position button above the selection
      // Account for button height (padding + text + border ≈ 40px) + spacing (12px)
      const buttonHeight = selectionButtonRef.current?.offsetHeight || 40;
      const spacing = 12;
      const buttonX = rect.left + rect.width / 2;
      const buttonY = rect.top + window.scrollY - buttonHeight - spacing;

      setSelectionState({
        text: selectedText,
        x: buttonX,
        y: buttonY,
      });
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't clear selection if clicking the selection button
      if (target.closest('[data-selection-button]')) {
        return;
      }
      // Clear selection on any other click
      if (selectionState) {
        setSelectionState(null);
      }
    };

    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectionState]);

  // If error creating from work, show error banner
  if (workInitError) {
    return (
      <>
        <TopBar
          ref={topBarRef}
          page={page}
          onToggle={handleTogglePage}
          onNavigateToOverview={handleNavigateToOverview}
        />
        <div
          style={{
            maxWidth: '1200px',
            margin: '32px auto',
            padding: '0 16px',
          }}
        >
          <Banner
            type="danger"
            actions={[
              {
                content: 'Reload page',
                onClick: () => window.location.reload(),
              },
            ]}
          >
            Error creating report from work: {workInitError}. Please try
            reloading the page.
          </Banner>
        </div>
      </>
    );
  }

  // If creating from work (or about to), show loading state with full landing page
  // Check workId to handle the initial render before isCreatingFromWork is set
  if (isCreatingFromWork || (workId && !reportId)) {
    return (
      <CiterightLanding
        isLoading
        userWorks={userWorks}
        testimonials={testimonials}
        faqs={faqs}
        onAnalyzeWork={handleAnalyzeWork}
      />
    );
  }

  // If no report_id in URL and not loading via public token, show landing page
  if (!reportId && !report) {
    if (!canEdit) {
      // Public viewer with no report loaded yet - show loading
      return (
        <PageWrapper>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
            }}
          >
            <Spinner size={48} />
          </div>
        </PageWrapper>
      );
    }
    return (
      <CiterightLanding
        userWorks={userWorks}
        testimonials={testimonials}
        faqs={faqs}
        onAnalyzeWork={handleAnalyzeWork}
        onReportCreated={handleReportCreated}
        showPreviousReports={showPreviousReports}
      />
    );
  }

  // If error loading XML
  if (xmlError) {
    return (
      <>
        <TopBar
          ref={topBarRef}
          page={page}
          onToggle={handleTogglePage}
          onNavigateToOverview={handleNavigateToOverview}
        />
        <PageWrapper>
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '32px 16px',
            }}
          >
            <Banner
              type="danger"
              actions={[
                {
                  content: 'Reload page',
                  onClick: () => window.location.reload(),
                },
              ]}
            >
              Error loading document: {xmlError}. Please try reloading the page.
            </Banner>
          </div>
        </PageWrapper>
      </>
    );
  }

  // If loading XML, show loading state
  if (xmlLoading || !xml) {
    if (!canEdit) {
      return (
        <PageWrapper>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
            }}
          >
            <Spinner size={48} />
          </div>
        </PageWrapper>
      );
    }
    return (
      <CiterightLanding
        isLoading
        userWorks={userWorks}
        testimonials={testimonials}
        faqs={faqs}
        onAnalyzeWork={handleAnalyzeWork}
      />
    );
  }

  return (
    <>
      <TopBar
        ref={topBarRef}
        page={page}
        onToggle={handleTogglePage}
        onNavigateToOverview={handleNavigateToOverview}
      >
        {claimCounts.total > 0 && (
          <FilterBar
            settings={filterSettings}
            onSettingsChange={setFilterSettings}
            essentialCount={claimCounts.essential}
            optionalCount={claimCounts.optional}
            currentClaimIndex={currentClaimIndex}
            totalClaims={claimCounts.total}
            onFirstClaim={goToMostImportant}
            onNextClaim={goToNext}
            onPreviousClaim={goToPrevious}
          />
        )}
      </TopBar>
      <ImpressionWatcher
        as={PageWrapper}
        source="citeright.inline_loaded"
        entity_id={reportId || undefined}
        threshold={0} //record if any part of this is visible
      >
        <LayoutWrapper>
          <MainContent ref={mainContentRef}>
            <Container>
              {parsedDoc.title && <Title>{parsedDoc.title}</Title>}

              <Section>
                {parsedDoc.bodyContent.map((item, index) => {
                  if (item.type === 'heading') {
                    // Headings have parts array, but should only contain strings
                    const headingText = item.parts
                      .filter(
                        (part): part is string => typeof part === 'string',
                      )
                      .join('');
                    return (
                      <SectionHeading key={index}>{headingText}</SectionHeading>
                    );
                  } else {
                    return (
                      <ParagraphWithClaims
                        key={index}
                        item={item}
                        firstClaimWithWorksId={firstClaimWithWorksId}
                        forceExpandedClaimId={forceExpandedClaimId}
                        claimRefs={claimRefs}
                        onCitationClick={handleCitationClick}
                        claimFilter={filterSettings.claimFilter}
                        filterSettings={filterSettings}
                        onClaimExpand={handleClaimExpand}
                      />
                    );
                  }
                })}
              </Section>

              <ReferencesSection
                references={report?.references || []}
                claims={report?.claims || []}
                onClaimClick={scrollToAndExpandClaim}
              />

              {page === 'debug' && (
                <UnmatchedClaimsSection claims={parsedDoc.unmatchedClaims} />
              )}
            </Container>
          </MainContent>

          {canEdit && page === 'debug' && (
            <Sidebar>
              <DebugPage />
            </Sidebar>
          )}
          {page === 'overview' && (
            <Sidebar ref={overviewRef}>
              <OverviewPage
                onClaimSelect={(claimId) => {
                  const claimRef = claimRefs.get(claimId)?.current;
                  if (claimRef) {
                    scrollTo(claimRef);
                  }
                }}
              />
            </Sidebar>
          )}
        </LayoutWrapper>
      </ImpressionWatcher>

      {canEdit && selectionState && (
        <SelectionButton
          ref={selectionButtonRef}
          data-selection-button
          style={{
            left: `${selectionState.x}px`,
            top: `${selectionState.y}px`,
            transform: 'translateX(-50%)',
          }}
          onClick={() => {
            if (context?.addClaim) {
              context.addClaim(selectionState.text);
              setSelectionState(null); // Clear selection after adding claim
              window.getSelection()?.removeAllRanges(); // Clear the text selection
            }
            trackClick({
              source: 'citeright.text_selection_button',
              entityId: context?.reportId ?? undefined,
            });
          }}
        >
          Add Claim
        </SelectionButton>
      )}
    </>
  );
};

interface CiterightInlineProps {
  userWorks?: UserWork[];
  testimonials?: Testimonial[];
  faqs?: FAQ[];
  publicToken?: string;
  canEdit?: boolean;
  showPreviousReports?: boolean;
}

export default function CiterightInline({
  userWorks,
  testimonials,
  faqs,
  publicToken,
  canEdit,
  showPreviousReports,
}: CiterightInlineProps) {
  return (
    <CiterightContextProvider publicToken={publicToken} canEdit={canEdit}>
      <CiterightInlineContent
        userWorks={userWorks}
        testimonials={testimonials}
        faqs={faqs}
        showPreviousReports={showPreviousReports}
      />
    </CiterightContextProvider>
  );
}
